import { Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { type MouseEventHandler, useState } from "react";
import {
  type RowClickFunction,
  CreateButton,
  useEditContext,
  useGetList,
  useNotify,
  useUpdate,
} from "react-admin";
import { type SubmitHandler } from "react-hook-form";

import { MODULE_NAMES } from "@/constants/modules";
import { LIST_QUERY_KEY } from "@/utils/query";

import { campaignTdsSchema } from "../api";

import {
  type CampaignTdsRecordsToCreate,
  CampaignTdsCreateFormModal,
} from "./CampaignTdsCreateFormModal";
import { CampaignTdsDataGrid } from "./CampaignTdsDataGrid";
import {
  type CampaignTdsRecordToEdit,
  CampaignTdsEditFormModal,
} from "./CampaignTdsEditFormModal";
import { type CampaignRecord } from "./types";

export const CampaignTdsEdit = () => {
  const [campaignToEdit, setCampaignToEdit] =
    useState<CampaignTdsRecordToEdit | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTdsCreating, setIsTdsCreating] = useState(false);
  const queryClient = useQueryClient();
  const notify = useNotify();
  const [update, { isPending: isUpdatePending }] = useUpdate();
  const { record } = useEditContext<CampaignRecord>();
  const campaignAtId = record?.["@id"];
  const { isPending, data } = useGetList(MODULE_NAMES.CAMPAIGN_TDS, {
    filter: {
      campaign: campaignAtId,
    },
  });

  const handleClickRow: RowClickFunction = (_index, _resource, record) => {
    setCampaignToEdit(record as unknown as CampaignTdsRecordToEdit);

    return false;
  };

  const handleCloseEditModal = () => {
    setCampaignToEdit(null);
  };

  const handleEditTds: SubmitHandler<CampaignTdsRecordToEdit> = async (
    values,
  ) => {
    await update(MODULE_NAMES.CAMPAIGN_TDS, { id: values.id, data: values });
    setCampaignToEdit(null);
    notify("Campaign TDS successfully updated!");
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleCreateTds: SubmitHandler<CampaignTdsRecordsToCreate> = async ({
    landings,
    countrySet,
    modelSet,
    osSet,
  }) => {
    const createCampaignTds = campaignTdsSchema.create;

    if (!createCampaignTds) {
      throw new Error("Create method of Campaign TDS is not defined");
    }

    setIsTdsCreating(true);

    const responses = await Promise.allSettled(
      landings.map((landing) =>
        createCampaignTds({
          data: {
            campaign: campaignAtId,
            landing,
            countrySet,
            modelSet,
            osSet,
          },
        }),
      ),
    );
    void queryClient.invalidateQueries({
      queryKey: [MODULE_NAMES.CAMPAIGN_TDS, LIST_QUERY_KEY],
    });

    const hasFailedRequests = responses.some(
      (response) => response.status === "rejected",
    );

    if (hasFailedRequests) {
      notify("Some requests failed", {
        type: "error",
      });
    }

    setIsTdsCreating(false);
    setIsCreateModalOpen(false);
  };

  const handleOpenCreateDialog: MouseEventHandler<HTMLAnchorElement> = (
    event,
  ) => {
    event.preventDefault();

    setIsCreateModalOpen(true);
  };

  return (
    <Stack direction="column" spacing={2} width="100%" marginTop="20px">
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h3" fontSize="16px">
          Campaign TDS
        </Typography>
        <CreateButton
          type="button"
          resource={MODULE_NAMES.CAMPAIGN_TDS}
          onClick={handleOpenCreateDialog}
        />
      </Stack>
      <CampaignTdsDataGrid
        data={data}
        isPending={isPending}
        onClickRow={handleClickRow}
        withDates
      />
      <CampaignTdsEditFormModal
        isOpen={campaignToEdit !== null}
        record={campaignToEdit}
        isPending={isUpdatePending}
        onClose={handleCloseEditModal}
        onSubmit={handleEditTds}
        withDates
      />
      <CampaignTdsCreateFormModal
        isOpen={isCreateModalOpen}
        isPending={isTdsCreating}
        onClose={handleCloseCreateModal}
        onSubmit={handleCreateTds}
      />
    </Stack>
  );
};
